/**
 * Dashboard Layout
 *
 * Main app shell with:
 * - Collapsible sidebar (256px expanded, 64px collapsed on tablet, hidden on mobile)
 * - Top header bar with breadcrumbs, search, notifications, user avatar
 * - Main content area with max-width 1152px
 * - Responsive: sidebar collapses at 1024px, becomes hamburger at 768px
 */

import { useState, useRef, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { AppProvider } from "@/contexts/app-context";
import { PostHogPageView } from "@/components/PostHogPageView";
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarItem,
} from "@platform/ui";
import {
  HomeIcon,
  LayoutGridIcon,
  FileEditIcon,
  BarChartIcon,
  BotIcon,
  SettingsIcon,
  HelpCircleIcon,
  MenuIcon,
  SearchIcon,
  BellIcon,
  UserIcon,
  LogOutIcon,
  ChevronDownIcon,
} from "lucide-react";

export function DashboardLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userMenuOpen]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const navigationItems = [
    {
      href: "/dashboard",
      label: "Dashboard",
      icon: <HomeIcon className="h-5 w-5" />,
    },
    {
      href: "/apps",
      label: "Apps",
      icon: <LayoutGridIcon className="h-5 w-5" />,
    },
    {
      href: "/content",
      label: "Content",
      icon: <FileEditIcon className="h-5 w-5" />,
    },
    {
      href: "/analytics",
      label: "Analytics",
      icon: <BarChartIcon className="h-5 w-5" />,
    },
    { href: "/agents", label: "Agents", icon: <BotIcon className="h-5 w-5" /> },
    {
      href: "/settings",
      label: "Settings",
      icon: <SettingsIcon className="h-5 w-5" />,
    },
    {
      href: "/help",
      label: "Help",
      icon: <HelpCircleIcon className="h-5 w-5" />,
    },
  ];

  // Check if a route is active (exact match or starts with the href)
  const isActiveRoute = (href: string) => {
    if (href === "/dashboard") {
      return pathname === href;
    }
    return pathname?.startsWith(href) ?? false;
  };

  return (
    <AppProvider>
      <PostHogPageView />
      <div className="flex h-screen bg-[var(--color-bg-primary)]">
        {/* Desktop Sidebar */}
        <Sidebar collapsed={sidebarCollapsed} className="hidden lg:flex">
          <SidebarHeader>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-md bg-[var(--color-primary-500)] flex items-center justify-center">
                <span className="text-white font-bold text-lg">F</span>
              </div>
              {!sidebarCollapsed && (
                <span className="text-xl font-bold text-[var(--color-text-primary)]">
                  FindOp
                </span>
              )}
            </div>
          </SidebarHeader>

          <SidebarContent>
            <nav className="space-y-1 px-2">
              {navigationItems.map((item) => (
                <SidebarItem
                  key={item.href}
                  href={item.href}
                  icon={item.icon}
                  collapsed={sidebarCollapsed}
                  active={isActiveRoute(item.href)}
                >
                  {item.label}
                </SidebarItem>
              ))}
            </nav>
          </SidebarContent>

          <SidebarFooter>
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="flex items-center justify-center w-full p-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)] rounded-md transition-colors"
              aria-label={
                sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
              }
            >
              <MenuIcon className="h-5 w-5" />
            </button>
          </SidebarFooter>
        </Sidebar>

        {/* Mobile Sidebar Overlay */}
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Mobile Sidebar */}
            <div className="fixed inset-y-0 left-0 z-50 w-64 bg-[var(--color-bg-secondary)] border-r border-[var(--color-border-default)] lg:hidden">
              <Sidebar collapsed={false} className="flex">
                <SidebarHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-md bg-[var(--color-primary-500)] flex items-center justify-center">
                        <span className="text-white font-bold text-lg">F</span>
                      </div>
                      <span className="text-xl font-bold text-[var(--color-text-primary)]">
                        FindOp
                      </span>
                    </div>
                    <button
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-1 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                      aria-label="Close menu"
                    >
                      <MenuIcon className="h-6 w-6" />
                    </button>
                  </div>
                </SidebarHeader>

                <SidebarContent>
                  <nav className="space-y-1 px-2">
                    {navigationItems.map((item) => (
                      <SidebarItem
                        key={item.href}
                        href={item.href}
                        icon={item.icon}
                        active={isActiveRoute(item.href)}
                      >
                        {item.label}
                      </SidebarItem>
                    ))}
                  </nav>
                </SidebarContent>
              </Sidebar>
            </div>
          </>
        )}

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="h-16 border-b border-[var(--color-border-default)] bg-[var(--color-bg-secondary)] flex items-center justify-between px-4 lg:px-6">
            {/* Left: Mobile menu + Breadcrumbs */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)] rounded-md"
                aria-label="Open menu"
              >
                <MenuIcon className="h-6 w-6" />
              </button>

              {/* Breadcrumbs */}
              <nav aria-label="Breadcrumb" className="hidden sm:block">
                <ol className="flex items-center space-x-2 text-sm text-[var(--color-text-secondary)]">
                  <li>
                    <Link
                      to="/dashboard"
                      className="hover:text-[var(--color-text-primary)]"
                    >
                      Dashboard
                    </Link>
                  </li>
                  {pathname &&
                    pathname !== "/dashboard" &&
                    pathname
                      .split("/")
                      .filter(Boolean)
                      .slice(0)
                      .map((segment, index, arr) => {
                        if (index === 0 && segment === "dashboard") return null;
                        const href = "/" + arr.slice(0, index + 1).join("/");
                        const label =
                          segment.charAt(0).toUpperCase() +
                          segment.slice(1).replace(/-/g, " ");
                        return (
                          <li
                            key={segment}
                            className="flex items-center space-x-2"
                          >
                            <span className="text-[var(--color-text-tertiary)]">
                              /
                            </span>
                            {index === arr.length - 1 ? (
                              <span className="text-[var(--color-text-primary)]">
                                {label}
                              </span>
                            ) : (
                              <Link
                                to={href}
                                className="hover:text-[var(--color-text-primary)]"
                              >
                                {label}
                              </Link>
                            )}
                          </li>
                        );
                      })}
                </ol>
              </nav>
            </div>

            {/* Right: Search, Notifications, User */}
            <div className="flex items-center gap-3">
              {/* Search */}
              <button
                className="p-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)] rounded-md"
                aria-label="Search (Cmd+K)"
              >
                <SearchIcon className="h-5 w-5" />
              </button>

              {/* Notifications */}
              <button
                className="relative p-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)] rounded-md"
                aria-label="Notifications"
              >
                <BellIcon className="h-5 w-5" />
                <span className="absolute top-1 right-1 h-2 w-2 bg-[var(--color-error-500)] rounded-full" />
              </button>

              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)] rounded-md"
                  aria-label="User menu"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  <div className="h-8 w-8 rounded-full bg-[var(--color-primary-500)] flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {user?.email?.[0]?.toUpperCase() || "U"}
                    </span>
                  </div>
                  <ChevronDownIcon
                    className={`h-4 w-4 transition-transform ${userMenuOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {/* Dropdown Menu */}
                {userMenuOpen && (
                  <div
                    className="absolute right-0 mt-2 w-64 bg-[var(--color-bg-elevated)] border border-[var(--color-border-default)] rounded-md shadow-[var(--shadow-lg)] z-50"
                    role="menu"
                  >
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-[var(--color-border-subtle)]">
                      <p className="text-sm font-medium text-[var(--color-text-primary)]">
                        {user?.email || "User"}
                      </p>
                      <p className="text-xs text-[var(--color-text-tertiary)] mt-1">
                        {user?.role || "user"}
                      </p>
                    </div>

                    {/* Menu Items */}
                    <div className="py-2">
                      <Link
                        to="/settings/profile"
                        className="flex items-center gap-3 px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)]"
                        role="menuitem"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <UserIcon className="h-4 w-4" />
                        Profile
                      </Link>
                      <Link
                        to="/settings"
                        className="flex items-center gap-3 px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)]"
                        role="menuitem"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <SettingsIcon className="h-4 w-4" />
                        Settings
                      </Link>
                    </div>

                    {/* Sign Out */}
                    <div className="border-t border-[var(--color-border-subtle)] py-2">
                      <button
                        onClick={handleSignOut}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-[var(--color-error-500)] hover:bg-[var(--color-error-50)] w-full text-left"
                        role="menuitem"
                      >
                        <LogOutIcon className="h-4 w-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto p-4 lg:p-6">
            <div className="max-w-[var(--width-content)] mx-auto">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </AppProvider>
  );
}
